import {
  Button as HeroUiButton,
  ButtonProps as HeroUiButtonProps,
  extendVariants,
} from '@heroui/react';
import { Loading } from '@ui/Loading';
import { isNil } from 'lodash';
import { ForwardRefExoticComponent, ForwardedRef, RefAttributes, forwardRef } from 'react';
import { PressEvent } from 'react-aria-components';
import { useOnPressAsync } from './useOnPressAsync';

export const Button = forwardRef(ButtonWithRef);
Button.displayName = 'Button';

export type ButtonVariant = 'solid' | 'outlined' | 'faded';
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl';
export type ButtonRadius = 'full' | 'square';
export type ButtonColor =
  | 'primary'
  | 'secondary'
  | 'white'
  | 'default'
  | 'active'
  | 'transparent'
  | 'danger';

export interface ButtonProps
  extends Omit<HeroUiButtonProps, 'variant' | 'color' | 'size' | 'radius' | 'onPress'> {
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  radius?: ButtonRadius;
  isInGroup?: boolean;
  onPress?: (event: PressEvent) => void | Promise<void>;
}

// https://heroui.org/docs/customization/custom-variants#creating-new-variants-for-non-slots-components
// https://github.com/heroui-inc/heroui/blob/main/packages/core/theme/src/components/button.ts#L22
const ButtonComponent = extendVariants(HeroUiButton, {
  variants: {
    variant: {
      solid: 'font-semibold', // solid bg color, no outline
      outlined: 'border-medium bg-transparent font-semibold', // no bg color, outlined
      faded: 'border-medium font-semibold', // light bg color, outlined
      // custom: '', // pass className, like for building tab buttons
    },
    color: {
      primary: 'bg-foreground text-background',
      secondary: 'text-foreground border-foreground bg-transparent data-[hover=true]:bg-background',
      white: 'bg-white text-white-foreground',
      default: 'bg-background text-foreground',
      active: 'bg-coast-green-10 text-coast-green-10-foreground',
      transparent: '',
      danger: 'bg-danger',
      // custom: '', // pass className, like for building tab buttons
    },
    size: {
      xs: 'px-3 text-xs min-w-12 h-6 gap-3 [&>svg]:w-6 [&>svg]:-mx-2',
      sm: 'px-5 text-sm min-w-16 h-8 gap-4 [&>svg]:w-6 [&>svg]:-mx-2',
      md: 'px-6 text-sm min-w-20 h-9 gap-6 [&>svg]:w-7 [&>svg]:-mx-4',
      lg: 'px-8 text-md min-w-24 h-10 gap-8 [&>svg]:w-8 [&>svg]:-mx-5',
      xl: 'px-10 text-lg min-w-28 h-12 gap-12 [&>svg]:w-10 [&>svg]:-mx-8',
      // custom: '', // pass className, like for building tab buttons
    },
    radius: {
      full: 'rounded-full',
      square: 'rounded-medium',
    },
    fullWidth: {
      true: 'w-full',
    },
    isDisabled: {
      true: 'opacity-disabled pointer-events-none',
    },
    isIconOnly: {
      true: 'px-0',
    },
    isInGroup: {
      true: '[&:not(:first-child):not(:last-child)]:rounded-none',
    },
    disableAnimation: {
      true: '!transition-none',
      false:
        'data-[pressed=true]:scale-[0.97] transition-transform-colors-opacity motion-reduce:transition-none',
    },
  },
  defaultVariants: {
    variant: 'solid',
    color: 'primary',
    size: 'md',
    isIconOnly: false,
    isDisabled: false,
    isInGroup: false,
    fullWidth: false,
    radius: 'full',
    disableAnimation: false,
  },
  compoundVariants: [
    // Tertiary faded borders
    {
      color: 'white',
      variant: 'faded',
      class: 'border-gray-10',
    },
    {
      color: 'default',
      variant: 'faded',
      class: 'border-gray-10',
    },
    {
      color: 'active',
      variant: 'faded',
      class: 'border-coast-green-30',
    },

    // Outline error
    {
      color: 'danger',
      variant: 'outlined',
      class: 'bg-white border-danger border-small',
    },

    // Icon only sizing
    {
      isIconOnly: true,
      size: 'xs',
      class: 'min-w-6 w-6 h-6 [&_svg]:w-5',
    },
    {
      isIconOnly: true,
      size: 'sm',
      class: 'min-w-8 w-8 h-8 [&_svg]:w-7',
    },
    {
      isIconOnly: true,
      size: 'md',
      class: 'min-w-9 w-9 h-9 [&_svg]:w-8',
    },
    {
      isIconOnly: true,
      size: 'lg',
      class: 'min-w-10 w-10 h-10 [&_svg]:w-9',
    },
    {
      isIconOnly: true,
      size: 'xl',
      class: 'min-w-12 w-12 h-12 [&_svg]:w-11',
    },

    // Button group
    {
      isInGroup: true,
      class:
        'rounded-none first:rounded-s-full last:rounded-e-full first:border-r-0 last:border-l-0',
    },

    // Split button (button group that starts ends with iconOnly)
    {
      isIconOnly: true,
      isInGroup: true,
      class: 'first:pl-1 last:pr-1',
    },
    {
      isIconOnly: true,
      isInGroup: true,
      color: 'primary',
      variant: 'solid',
      class: 'first:border-r-1 first:border-r-white last:border-l-1 last:border-l-white',
    },
    {
      isIconOnly: true,
      isInGroup: true,
      color: 'secondary',
      variant: 'outlined',
      class: 'first:border-r-1 first:border-r-foreground last:border-l-1 last:border-l-foreground',
    },
    {
      isIconOnly: true,
      isInGroup: true,
      color: 'white',
      variant: 'faded',
      class: 'first:border-r-1 first:border-r-gray-10 last:border-l-1 last:border-l-gray-10',
    },
    {
      isIconOnly: true,
      isInGroup: true,
      color: 'default',
      variant: 'faded',
      class: 'first:border-r-1 first:border-r-gray-10 last:border-l-1 last:border-l-gray-10',
    },
    {
      isIconOnly: true,
      isInGroup: true,
      color: 'active',
      variant: 'faded',
      class:
        'first:border-r-1 first:border-coast-green-30 last:border-l-1 last:border-coast-green-30',
    },
  ],
}) as ForwardRefExoticComponent<Omit<ButtonProps, 'ref'> & RefAttributes<HTMLButtonElement | null>>;

function ButtonWithRef(props: ButtonProps, ref: ForwardedRef<HTMLButtonElement | null>) {
  const {
    children,
    variant = 'solid',
    color = 'primary',
    size = 'md',
    radius = 'full',
    onClick,
    ...rest
  } = props;

  // Upstream heroui components PopoverTrigger and DropdownTrigger are hanging an onClick on Button
  // which is causing a warning in the console. No buttons should have onClick.
  // https://github.com/heroui-inc/heroui/issues/4399
  if (!isNil(onClick) && rest['data-slot' as keyof typeof rest] !== 'trigger') {
    throw new Error('Button onClick is deprecated. Use onPress instead.');
  }

  const [isLoading, onPress] = useOnPressAsync(props);
  const spinner = <ButtonSpinner size={size} />;

  return (
    <ButtonComponent
      {...rest}
      ref={ref}
      onClick={onClick}
      onPress={onPress}
      isLoading={isLoading}
      variant={variant}
      color={color}
      size={size}
      radius={radius}
      spinner={spinner}
    >
      <span className="group-data-[loading=true]:opacity-0">{children}</span>
    </ButtonComponent>
  );
}

function ButtonSpinner({ size }: { size: ButtonSize | undefined }) {
  return <Loading className="absolute" size={size} />;
}
